import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.reducer';
import { AdminWalletsService } from '../../services/admin-wallets.service';
import { AdminWallet } from '../../interfaces/admin-wallet.interface';
import {
  BinanceDepositService,
  WalletCardexUser,
} from '../../services/binance-deposit.service';
import { BinanceWalletClientService } from '../../services/binance-wallet-client.service';
import { BinanceWithdrawalService, BalanceResponse } from '../../services/binance-withdrawal.service';
import {
  BinanceWallet,
  MyDepositAddressResponse,
} from '../../interfaces/binance-wallet.interface';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-load-pays-binance',
  templateUrl: './load-pays-binance.component.html',
  styleUrls: ['./load-pays-binance.component.sass'],
})
export class LoadPaysBinanceComponent implements OnInit {
  isLoading = false;
  navigatingToConvert = false; // Loading para navegación a convertir CLP
  navigatingToConvertVes = false; // Loading para navegación a convertir VES
  navigatingToSend = false; // Loading para navegación a enviar
  errorMessage = '';
  modalErrorMessage = ''; // Errores específicos del modal
  showModal = false;
  selectedNetwork = '';
  txId = '';
  availableNetworks: string[] = []; // Redes únicas desde BD
  selectedWallet: AdminWallet | null = null; // Wallet seleccionada
  loadingWalletsInModal = false; // Loader para el modal
  walletsForDeposit: AdminWallet[] = []; // Wallets cargadas
  copiedInDepositModal = false; // Estado de "Copiado!"
  isConfirming = false;

  showWalletsModal = false;
  wallets: AdminWallet[] = [];
  walletsLoading = false;
  walletsError = '';
  copiedWalletId: string | null = null;

  userId: string = '';

  cardex: WalletCardexUser[] = [];
  cardexTotal = 0;
  cardexPage = 1;
  cardexLimit = 20;
  cardexLoading = false;

  saldoActual: number = 0;
  saldoUsdtTotal: number = 0;
  saldoUsdtBloqueado: number = 0;
  saldoUsdtDisponible: number = 0;

  tipoId: string = '';
  userLoaded: boolean = false;
  expandedCardId: string | null = null;

  /**
   * Wallet Binance Link propia del usuario, devuelta por
   * `GET /binance-link/wallets/me`. `null` si nunca creó (404) o si la
   * última request falló.
   */
  myBinanceWallet: BinanceWallet | null = null;
  loadingMyWallet = false;

  /**
   * Dirección de depósito TRC20 del usuario obtenida con
   * `GET /me/deposit-address`. Se cachea en memoria de la vista — la
   * address TRC20 es estable y el endpoint hace una llamada live a
   * Binance, no conviene re-pedirla en cada render.
   *
   * Si la promesa resuelve con `null` (404 sin wallet o 409 KYC
   * pendiente), el componente cae al admin wallet por default. El
   * cliente final NO ve "KYC pendiente" como un estado bloqueante.
   */
  myDepositAddress: MyDepositAddressResponse | null = null;
  loadingMyDepositAddress = false;

  /**
   * Kill-switch de Binance Link. Cuando está deshabilitado, la recarga NO
   * consulta la wallet/deposit-address personal del usuario: siempre usa
   * las wallets administrativas (con confirmación por TXID). Mismo flag que
   * oculta el panel de operador en el navbar.
   */
  readonly binanceLinkEnabled: boolean = environment.binanceLinkEnabled === true;

  toggleCard(id: string) {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  constructor(
    private adminWalletsService: AdminWalletsService,
    private binanceDepositService: BinanceDepositService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private binanceWalletClientService: BinanceWalletClientService,
    private cognitoService: CognitoService,
    private store: Store<AppState>,
    private router: Router,
  ) {
    this.store.select('auth').subscribe(({ user }) => {
      if (user && user.usuarioId) {
        this.userId = String(user.usuarioId);
        this.tipoId = user.tipoId || '';
        this.userLoaded = true;
        localStorage.setItem('userId', this.userId);
      }
    });
  }

  ngOnInit(): void {
    this.fetchSaldoActual();
    this.fetchCardex();
    // Binance Link deshabilitado: no consultamos la wallet/deposit-address
    // personal → `myDepositAddress`/`myBinanceWallet` quedan null y la
    // recarga cae a las wallets administrativas (flujo con TXID).
    if (this.binanceLinkEnabled) {
      this.loadMyBinanceWallet();
      this.loadMyDepositAddress();
    }
    this.abrirModalSiVieneDeRecargarUsdt();
  }

  /**
   * Abre el modal de depósito de una sola vez cuando se llega desde el
   * botón "Recargar USDT" del home, que navega con
   * `state: { openUsdtRechargeModal: true }`.
   *
   * El flag se *consume* del history state (replaceState) para que un
   * refresh de la página no lo vuelva a abrir. Cualquier otra entrada a
   * la vista — menú, link directo, retorno desde convertir/enviar,
   * cotizador — sigue mostrando la pantalla sin modal.
   */
  private abrirModalSiVieneDeRecargarUsdt(): void {
    const state = (window.history.state ?? {}) as Record<string, unknown>;
    if (!state['openUsdtRechargeModal']) return;

    const { openUsdtRechargeModal, ...resto } = state;
    window.history.replaceState(resto, '');

    this.openModal();
  }

  /**
   * Carga única (al entrar a la vista) de la dirección de depósito
   * TRC20 del usuario. Si retorna null (404 sin wallet / 409 KYC
   * pendiente), el getter `depositAddress` cae al admin wallet
   * automáticamente.
   */
  private async loadMyDepositAddress(): Promise<void> {
    this.loadingMyDepositAddress = true;
    try {
      this.myDepositAddress =
        await this.binanceWalletClientService.getMyDepositAddress();
    } catch (err) {
      // 5xx / 502 — log y caer al admin wallet por default.
      console.warn(
        '[LoadPaysBinance] Error obteniendo mi deposit-address',
        err,
      );
      this.myDepositAddress = null;
    } finally {
      this.loadingMyDepositAddress = false;
    }
  }

  /**
   * Trae la wallet Binance Link propia del usuario. Si retorna `null`
   * (404 = sin wallet) se cae al fallback de admin wallets. Otros errores
   * se loguean en consola — el flujo de recarga sigue funcionando con
   * las wallets administrativas.
   */
  private async loadMyBinanceWallet(): Promise<void> {
    this.loadingMyWallet = true;
    try {
      this.myBinanceWallet =
        await this.binanceWalletClientService.getMyWallet();
    } catch (err) {
      console.warn('[LoadPaysBinance] Error obteniendo mi wallet', err);
      this.myBinanceWallet = null;
    } finally {
      this.loadingMyWallet = false;
    }
  }

  /**
   * `true` cuando el usuario tiene wallet propia operativa Y la red
   * elegida es TRC20. En ese caso el modal muestra SU sub-cuenta como
   * destino de depósito en lugar del wallet admin.
   */
  get useMyBinanceWalletForTrc20(): boolean {
    if (!this.myBinanceWallet) return false;
    if (this.selectedNetwork !== 'TRX' && this.selectedNetwork !== 'TRC20') {
      return false;
    }
    return this.myBinanceWallet.walletStatus === 'ACTIVE';
  }

  /**
   * Dirección de depósito que se muestra en el modal.
   *
   * Prioridad:
   *  1. Si la red elegida es TRC20 y tenemos cacheada la deposit
   *     address del usuario (`/me/deposit-address` resolvió a un objeto),
   *     se muestra esa dirección.
   *  2. Caso contrario (red distinta, sin wallet=404, KYC pendiente=409,
   *     o error de red contra Binance), se cae al admin wallet por
   *     default seleccionado. Esto preserva el flujo de recarga aunque
   *     el usuario no tenga su propia sub-cuenta operativa.
   *
   * Importante: la UI no expone ningún detalle de Binance Link al
   * cliente final — solo la address en sí.
   */
  get depositAddress(): string | null {
    const networkIsTrc20 =
      this.selectedNetwork === 'TRX' || this.selectedNetwork === 'TRC20';
    if (networkIsTrc20 && this.myDepositAddress?.address) {
      return this.myDepositAddress.address;
    }
    return this.selectedWallet?.address ?? null;
  }

  /**
   * `true` cuando la address mostrada en el modal corresponde a la
   * wallet propia del usuario (vía `/me/deposit-address`) en lugar del
   * admin wallet por default. En este caso el depósito se acredita
   * automáticamente: no se necesita TXID ni paso de confirmación.
   */
  get usingPersonalWallet(): boolean {
    const networkIsTrc20 =
      this.selectedNetwork === 'TRX' || this.selectedNetwork === 'TRC20';
    return !!(networkIsTrc20 && this.myDepositAddress?.address);
  }

  /** Copia al portapapeles la dirección actualmente visible. */
  copyDepositAddress(): void {
    const address = this.depositAddress;
    if (!address || !navigator?.clipboard) return;
    navigator.clipboard.writeText(address).then(() => {
      this.copiedInDepositModal = true;
      setTimeout(() => {
        this.copiedInDepositModal = false;
      }, 2000);
    });
  }

  openModal() {
    this.showModal = true;
    this.txId = '';
    this.selectedNetwork = '';
    this.selectedWallet = null;
    this.copiedInDepositModal = false;
    this.modalErrorMessage = '';

    // Cargar wallets al abrir el modal
    this.loadingWalletsInModal = true;
    this.adminWalletsService.findAllActive().subscribe({
      next: (wallets) => {
        this.walletsForDeposit = wallets;
        // Extraer redes únicas
        this.availableNetworks = [...new Set(wallets.map((w) => w.network))];
        // Preseleccionar Tron (TRX/TRC20) por defecto; si no está, cae a la
        // primera red disponible. Así la wallet de depósito se muestra de
        // inmediato al abrir, sin el paso "-- Selecciona una red --".
        const tron = this.availableNetworks.find(
          (n) => n === 'TRX' || n === 'TRC20',
        );
        this.selectedNetwork = tron || this.availableNetworks[0] || '';
        this.onNetworkChange();
        this.loadingWalletsInModal = false;
      },
      error: (err) => {
        this.modalErrorMessage = err?.message || 'Error al cargar wallets.';
        this.loadingWalletsInModal = false;
        // Auto-limpiar error después de 3 segundos
        setTimeout(() => {
          this.modalErrorMessage = '';
        }, 3000);
      },
    });
  }

  closeModal() {
    this.showModal = false;
  }

  navigateToConvert() {
    if (this.navigatingToConvert) return;
    this.navigatingToConvert = true;
    this.router.navigate(['/wallet/client/convert-usdt-clp']).finally(() => {
      this.navigatingToConvert = false;
    });
  }

  navigateToConvertVes() {
    if (this.navigatingToConvertVes) return;
    this.navigatingToConvertVes = true;
    this.router.navigate(['/wallet/client/convert-usdt-ves']).finally(() => {
      this.navigatingToConvertVes = false;
    });
  }

  navigateToSend() {
    if (this.navigatingToSend) return;
    this.navigatingToSend = true;
    this.router.navigate(['/wallet/client/send-usdt']).finally(() => {
      this.navigatingToSend = false;
    });
  }

  /**
   * Nombre completo de la red para mostrarlo junto al código. Solo es un
   * label visual: el `value` del option sigue siendo el código (TRX/ETH/BSC),
   * así que el flujo de recargas no cambia. Cubre también los alias TRC20/
   * ERC20/BEP20 por si la BD guarda la red con esos nombres.
   */
  private static readonly NETWORK_NAMES: Record<string, string> = {
    TRX: 'Tron (TRC20)',
    TRC20: 'Tron (TRC20)',
    ETH: 'Ethereum (ERC20)',
    ERC20: 'Ethereum (ERC20)',
    BSC: 'BNB Smart Chain (BEP20)',
    BEP20: 'BNB Smart Chain (BEP20)',
  };

  /** Devuelve el nombre completo de la red, o el mismo código si no está mapeado. */
  networkLabel(code: string): string {
    return LoadPaysBinanceComponent.NETWORK_NAMES[code] ?? code;
  }

  onNetworkChange() {
    this.selectedWallet = null;
    this.copiedInDepositModal = false;

    if (this.selectedNetwork) {
      // Filtrar wallet por red seleccionada
      const wallet = this.walletsForDeposit.find(
        (w) => w.network === this.selectedNetwork,
      );
      this.selectedWallet = wallet || null;
    }
  }

  openModalWalletDisponibles() {
    this.showWalletsModal = true;
    this.copiedWalletId = null;
    this.walletsLoading = true;
    this.walletsError = '';
    this.adminWalletsService.findAllActive().subscribe({
      next: (wallets) => {
        this.wallets = wallets;
        this.walletsLoading = false;
      },
      error: (err) => {
        this.walletsError = err?.message || 'Error al cargar wallets.';
        this.walletsLoading = false;
        // Auto-limpiar error después de 5 segundos
        setTimeout(() => {
          this.walletsError = '';
        }, 5000);
      },
    });
  }

  closeWalletsModal() {
    this.showWalletsModal = false;
    this.copiedWalletId = null;
  }

  copyWallet(wallet: AdminWallet) {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(wallet.address).then(() => {
        this.copiedWalletId = wallet.id;
        setTimeout(() => {
          this.closeWalletsModal();
        }, 1200);
      });
    }
  }

  copyWalletInDepositModal() {
    if (this.selectedWallet && navigator?.clipboard) {
      navigator.clipboard.writeText(this.selectedWallet.address).then(() => {
        this.copiedInDepositModal = true;
        setTimeout(() => {
          this.copiedInDepositModal = false;
        }, 2000);
      });
    }
  }

  onTxIdInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.txId = input.value.replace(/\s+/g, ''); // Quita espacios
  }

  async confirmReload() {
    if (!this.selectedNetwork || !this.txId) {
      this.modalErrorMessage = 'Por favor, completa todos los campos.';
      setTimeout(() => {
        this.modalErrorMessage = '';
      }, 3000);
      return;
    }

    if (!this.userId) {
      this.modalErrorMessage = 'No se pudo obtener el usuario autenticado.';
      setTimeout(() => {
        this.modalErrorMessage = '';
      }, 3000);
      return;
    }

    this.isConfirming = true;
    this.modalErrorMessage = '';
    this.binanceDepositService
      .confirmDeposit({
        userId: this.userId,
        txId: this.txId,
        network: this.selectedNetwork,
      })
      .subscribe({
        next: (res: any) => {
          this.closeModal();
          this.refreshAll(); // Actualiza cardex y saldo
          this.isConfirming = false;
        },
        error: (err: any) => {
          this.modalErrorMessage =
            err?.message || 'Error al confirmar depósito.';
          this.isConfirming = false;
          // Auto-limpiar error después de 10 segundos: los textos del API
          // (fuera de plazo, ir a soporte) no se alcanzan a leer en 3.
          setTimeout(() => {
            this.modalErrorMessage = '';
          }, 10000);
        },
      });
  }

  fetchCardex(page: number = 1) {
    this.cardexLoading = true;
    this.binanceDepositService
      .getUserCardex(this.userId, page, this.cardexLimit)
      .subscribe({
        next: (res) => {
          this.cardex = res.items;
          this.cardexTotal = res.total;
          this.cardexPage = res.page;
          this.cardexLimit = res.limit;
          this.cardexLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.message || 'Error al cargar movimientos.';
          this.cardexLoading = false;
          // Auto-limpiar error después de 5 segundos
          setTimeout(() => {
            this.errorMessage = '';
          }, 5000);
        },
      });
  }

  async fetchSaldoActual() {
    try {
      // Obtener balance desglosado (total, bloqueado, disponible)
      const balanceObservable = await this.binanceWithdrawalService.getBalance();
      const balance: BalanceResponse = await firstValueFrom(balanceObservable);

      this.saldoUsdtTotal = balance.totalBalance || 0;
      this.saldoUsdtBloqueado = balance.blockedBalance || 0;
      this.saldoUsdtDisponible = balance.availableBalance || 0;

      // Mantener compatibilidad con código existente
      this.saldoActual = balance.availableBalance || 0;
    } catch (err: any) {
      this.saldoUsdtTotal = 0;
      this.saldoUsdtBloqueado = 0;
      this.saldoUsdtDisponible = 0;
      this.saldoActual = 0;
      this.errorMessage = err?.message || 'Error al obtener saldo.';
      // Auto-limpiar error después de 5 segundos
      setTimeout(() => {
        this.errorMessage = '';
      }, 5000);
    }
  }

  onCardexPageChange(event: any) {
    this.cardexLimit = event.pageSize;
    this.fetchCardex(event.pageIndex + 1);
  }

  refreshAll() {
    this.fetchCardex();
    this.fetchSaldoActual();
  }

  get totalPages(): number {
    return this.cardexTotal > 0
      ? Math.ceil(this.cardexTotal / this.cardexLimit)
      : 1;
  }

  /**
   * Sanitiza la descripción removiendo el email del operador
   * Ej: "Debitado manualmente por operador: email@test.com | Motivo: xxx"
   *  -> "Debitado manualmente por operador | Motivo: xxx"
   */
  sanitizeDescription(description: string | undefined): string {
    if (!description) return '';
    // Remueve el patrón ": email@dominio.com" antes del " | "
    return description.replace(/:\s*[^\s|]+@[^\s|]+\s*\|/g, ' |');
  }

  /**
   * Obtiene la etiqueta descriptiva para el campo network/red
   */
  getNetworkLabel(network: string | undefined): string {
    if (!network) return '';
    const networkLabels: { [key: string]: string } = {
      SWAP_USDT_CLP: 'Conversión USDT→CLP',
      SWAP_CLP_TO_USDT: 'Conversión CLP→USDT',
      SWAP_USDT_VES: 'Swap USDT→VES (beneficiario)',
      SWAP_USDT_VES_WALLET: 'Swap USDT→VES (wallet)',
      SWAP_VES_TO_USDT: 'Retiro VES→USDT',
      CONVERSION: 'Conversión USDT→CLP',
      SEND_USDT: 'Retiro USDT',
      DEPOSIT_USDT: 'Depósito USDT',
      MANUAL_LOAD_USDT: 'Carga Manual USDT',
      ADD_USDT_MANUAL: 'Carga Manual USDT',
      DEBIT_USDT_MANUAL: 'Débito Manual USDT',
      REVERSO_SWAP_USDT_VES: 'Reverso Swap USDT→VES',
      REVERSO_RETIRO_USDT_CLP: 'Reverso Retiro USDT→CLP',
      REVERSO_RETIRO: 'Reverso Retiro',
      RETIRO_USDT_CLP: 'Retiro USDT→CLP',
      // Remesas Vita. Faltaban, y el backend devolvía DEPOSIT_USDT para todo lo
      // que no reconocía: el cargo de una remesa y el reverso de una remesa
      // rechazada se mostraban como "Depósito USDT" al lado de su propio cargo.
      PAYOUT_USDT_COP: 'Envío a Colombia (COP)',
      REVERSO_USDT_COP: 'Reverso envío COP',
      PAYOUT_USDT_PEN: 'Envío a Perú (PEN)',
      REVERSO_USDT_PEN: 'Reverso envío PEN',
    };
    return networkLabels[network] || network;
  }
}
